import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeader,
} from "V2.0/common/table";
import { Button } from "V2.0/common/button";
import { Badge } from "V2.0/common/badge";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "V2.0/common/dropdown.jsx";
import {
  Dialog,
  DialogTitle,
  DialogActions,
} from "V2.0/components/DialogFullWidth";
import { message } from "antd";
import { formatTime, showErrorToast } from "V2.0/utils";
import {
  ArrowLeftIcon,
  EllipsisHorizontalIcon,
  PlayIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { getModelName } from "../../ModelSettings/GPTResponseModelCategorized/GPTResponsePremiumModel";
import EvalRunDetail from "../EvalRun";
import EvalRunCreateDialog from "../EvalRunCreate/EvalRunCreateDialog";
import EvalTemplateCopy from "../EvalTemplateCopy";
import useEvalRun from "../useEvalRun";
import {
  getEvalTemplates,
  postCreateEvalTemplate,
  putUpdateEvalTemplate,
  patchEvalTemplateActive,
  postCopyEvalTemplate,
  deleteEvalRun,
} from "../service";

export default AutomatedTesting;

const COMPLETED = "completed";

const View = {
  TEMPLATES: "templates",
  HISTORY: "history",
} as const;

function computeOverallScore(evalRun): number | null {
  return typeof evalRun?.overallScore === "number" ? evalRun.overallScore : null;
}

/** Expected-event assertions that held. */
function computeEventPassRate(evalRun): { passed: number; total: number } {
  const eventScores = (evalRun?.results ?? []).flatMap(
    (result) => result?.eventScores ?? [],
  );

  const passed = eventScores.filter(
    (eventScore) => eventScore.occurred === eventScore.expected,
  ).length;

  return { passed, total: eventScores.length };
}

function formatScore(score: number | null): string {
  if (score === null || Number.isNaN(score)) return "—";
  return score.toFixed(1);
}

function groupRunsByTemplate(evalRuns) {
  const byTemplate = new Map<string, any[]>();

  for (const evalRun of evalRuns) {
    const key = evalRun?.template;
    if (!key) continue;

    const id = String(key);
    if (!byTemplate.has(id)) byTemplate.set(id, []);
    byTemplate.get(id).push(evalRun);
  }

  return byTemplate;
}

function statusColor(status) {
  if (status === COMPLETED) return "green";
  if (status === "running") return "blue";
  if (status === "failed") return "red";
  if (status === "stopped") return "amber";
  return "zinc";
}

function isSettling(status): boolean {
  return status === "pending" || status === "running";
}


function useEvalTemplates(workspaceId, assistantId) {
  const [templates, setTemplates] = useState([]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await getEvalTemplates({
        params: { workspaceId, assistantId },
        queryParams: { page: 1, limit: 100 },
      });
      setTemplates(response.data?.templates ?? []);
    } catch (e) {
      setTemplates([]);
    }
  }, [workspaceId, assistantId]);

  return { templates, fetchTemplates };
}

function ScoreDelta(props) {
  const { delta, className = "" } = props;

  if (delta === null || delta === undefined) {
    return (
      <span className={`text-xs text-zinc-400 dark:text-zinc-500 ${className}`}>
        first run
      </span>
    );
  }

  const rounded = Math.round(delta * 10) / 10;

  if (rounded === 0) {
    return (
      <span
        className={`inline-flex items-center rounded bg-zinc-100 px-1.5 py-0.5 text-xs font-medium tabular-nums text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400 ${className}`}
      >
        no change
      </span>
    );
  }

  const isUp = rounded > 0;
  const tone = isUp
    ? "bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
    : "bg-red-500/15 text-red-700 dark:bg-red-500/10 dark:text-red-400";

  return (
    <span
      className={`inline-flex items-center gap-x-0.5 rounded px-1.5 py-0.5 text-xs font-medium tabular-nums ${tone} ${className}`}
    >
      {isUp ? "▲" : "▼"} {Math.abs(rounded).toFixed(1)}
    </span>
  );
}

function Stat(props) {
  const { label, value, unit, meta } = props;

  return (
    <div className="bg-white p-4 dark:bg-zinc-900">
      <p className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-zinc-900 dark:text-white">
        {value}
        {unit && (
          <span className="ml-0.5 text-sm font-normal text-zinc-400 dark:text-zinc-500">
            {unit}
          </span>
        )}
      </p>
      <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{meta}</div>
    </div>
  );
}

function Section(props) {
  const { title, children } = props;

  return (
    <div>
      <p className="mb-2 text-sm font-medium text-zinc-900 dark:text-white">
        {title}
      </p>
      {children}
    </div>
  );
}

function Group(props) {
  const { title, subtitle, children } = props;

  return (
    <div>
      <div className="mb-3 border-b border-zinc-200 pb-2 dark:border-zinc-700">
        <p className="text-base font-semibold text-zinc-900 dark:text-white">
          {title}
        </p>
        {subtitle && (
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </div>
  );
}

function FieldGrid(props) {
  const { fields } = props;

  return (
    <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-zinc-200 bg-zinc-200 dark:border-zinc-700 dark:bg-zinc-700 sm:grid-cols-2 lg:grid-cols-3">
      {fields.map(([label, value], idx) => (
        <div key={idx} className="bg-white p-3 dark:bg-zinc-900">
          <p className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            {label}
          </p>
          <p className="mt-0.5 break-words text-sm text-zinc-800 dark:text-zinc-100">
            {value}
          </p>
        </div>
      ))}
    </div>
  );
}

function TemplatesTable(props) {
  const {
    templates,
    onOpenHistory,
    onRunTemplate,
    onEditTemplate,
    onSetTemplateActive,
    locked,
  } = props;

  return (
    <div className="overflow-auto">
      <Table>
        <TableHead>
          <TableHeader>Test</TableHeader>
          <TableHeader>Models</TableHeader>
          <TableHeader>Latest score</TableHeader>
          <TableHeader>Last run</TableHeader>
          <TableHeader>Runs</TableHeader>
          <TableHeader>Messages used</TableHeader>
          <TableHeader></TableHeader>
        </TableHead>
        <TableBody>
          {templates.map((template) => {
            const models = template.config?.modelsToTest ?? [];
            const personas = template.config?.personas ?? [];
            const criteria = template.config?.qualityCriteria ?? [];

            const isInactive = template.isActive === false;

            return (
              <TableRow
                key={String(template._id)}
                className={isInactive ? "opacity-50" : ""}
              >
                <TableCell>
                  <button
                    type="button"
                    onClick={() => onOpenHistory(template)}
                    className="text-left font-medium text-zinc-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400"
                  >
                    {template.name}
                  </button>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Badge color="zinc">
                      {template.config?.simulationMode ?? "voice"}
                    </Badge>
                    {isInactive && <Badge color="amber">Deactivated</Badge>}
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">
                      {personas.length} personas · {criteria.length} criteria
                    </span>
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex flex-wrap items-center gap-1">
                    {models.map((model, idx) => (
                      <Badge key={idx}>{getModelName(model)}</Badge>
                    ))}
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex items-baseline gap-x-1">
                    <span className="text-lg font-semibold tabular-nums text-zinc-900 dark:text-white">
                      {formatScore(template.latestScore)}
                    </span>
                    <span className="text-xs text-zinc-400 dark:text-zinc-500">
                      /10
                    </span>
                  </div>
                </TableCell>

                <TableCell>
                  <span className="text-sm text-zinc-600 dark:text-zinc-300">
                    {template.latestRun?.createdAt
                      ? formatTime(template.latestRun.createdAt)
                      : "—"}
                  </span>
                  <div className="mt-1">
                    <Badge color={statusColor(template.latestRun?.status)}>
                      {template.latestRun?.status ?? "unknown"}
                    </Badge>
                  </div>
                </TableCell>

                <TableCell>
                  <span className="tabular-nums text-sm text-zinc-600 dark:text-zinc-300">
                    {template.runCount}
                  </span>
                </TableCell>

                <TableCell>
                  <span className="tabular-nums text-sm text-zinc-600 dark:text-zinc-300">
                    {(template.messagesUsed ?? 0).toLocaleString()}
                  </span>
                </TableCell>

                <TableCell>
                  <Dropdown>
                    <DropdownButton plain className="rounded-full">
                      <EllipsisHorizontalIcon />
                    </DropdownButton>
                    <DropdownMenu>
                      <DropdownItem onClick={() => onOpenHistory(template)}>
                        View history
                      </DropdownItem>
                      {/* A deactivated test can't start new runs, so Run and
                          Edit are withheld until it's switched back on. */}
                      {!locked && !isInactive && (
                        <DropdownItem onClick={() => onRunTemplate(template)}>
                          Run again
                        </DropdownItem>
                      )}
                      {!locked && !isInactive && (
                        <DropdownItem onClick={() => onEditTemplate(template)}>
                          Edit settings
                        </DropdownItem>
                      )}
                      {/* No confirmation: this is reversible and destroys
                          nothing — the runs and their message history stay. */}
                      {!locked && template._id && (
                        <DropdownItem
                          onClick={() =>
                            onSetTemplateActive(template, isInactive)
                          }
                        >
                          {isInactive ? "Reactivate" : "Deactivate"}
                        </DropdownItem>
                      )}
                    </DropdownMenu>
                  </Dropdown>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Every run produced by one template, numbered per template (#1 is its first
 * ever run) so a run can be referred to without quoting a timestamp or an id.
 */
function RunHistory(props) {
  const {
    template,
    onBack,
    onViewRun,
    onViewCheckpoint,
    onRunTemplate,
    onEditTemplate,
    onDeleteRun,
    locked,
  } = props;

  const rows = useMemo(() => {
    const numbered = (template.runs ?? [])
      .slice()
      .reverse()
      .map((evalRun, idx) => ({
        evalRun,
        runNumber: idx + 1,
        score: computeOverallScore(evalRun),
        events: computeEventPassRate(evalRun),
        delta: null as number | null,
      }));
    let lastScored: number | null = null;
    for (const row of numbered) {
      row.delta =
        row.score !== null && lastScored !== null ? row.score - lastScored : null;
      if (row.score !== null) lastScored = row.score;
    }

    return numbered.reverse();
  }, [template]);

  const latest = rows.find((row) => row.score !== null);

  return (
    <div className="flex flex-col gap-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Button plain onClick={onBack} className="-ml-2 mb-1">
            <ArrowLeftIcon />
            All tests
          </Button>
          <p className="text-lg font-semibold text-zinc-900 dark:text-white">
            {template.name}
          </p>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            {template.config?.simulationMode ?? "voice"} ·{" "}
            {(template.config?.personas ?? []).length} personas ·{" "}
            {(template.config?.qualityCriteria ?? []).length} criteria ·{" "}
            {(template.config?.modelsToTest ?? []).length} models ·{" "}
            {template.config?.maxTurns ?? 8} turns max
          </p>
        </div>

        {!locked && (
          <div className="flex items-center gap-x-2">
            <Button outline onClick={() => onEditTemplate(template)}>
              <PencilSquareIcon />
              Edit settings
            </Button>
            <Button color="blue" onClick={() => onRunTemplate(template)}>
              <PlayIcon />
              Run again
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-zinc-200 bg-zinc-200 dark:border-zinc-700 dark:bg-zinc-700">
        <Stat
          label="Latest score"
          value={formatScore(latest?.score ?? null)}
          unit="/10"
        />
        <Stat
          label="Times run"
          value={String(template.runCount ?? (template.runs ?? []).length)}
          meta="since this test was created"
        />
      </div>

      <div>
        <div className="mb-2">
          <p className="text-sm font-medium text-zinc-900 dark:text-white">
            Run history
          </p>
        </div>

        <div className="overflow-auto">
          <Table>
            <TableHead>
              <TableHeader>Run</TableHeader>
              <TableHeader>Score</TableHeader>
              <TableHeader>Events</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Checkpoint</TableHeader>
              <TableHeader>Messages</TableHeader>
              <TableHeader></TableHeader>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.evalRun?._id ?? row.runNumber}>
                  <TableCell>
                    <span className="font-medium text-zinc-900 dark:text-white">
                      #{row.runNumber}
                    </span>
                    <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                      {formatTime(row.evalRun?.createdAt)}
                    </div>
                  </TableCell>

                  <TableCell>
                    <span className="text-base font-semibold tabular-nums text-zinc-900 dark:text-white">
                      {formatScore(row.score)}
                    </span>
                    <span className="ml-0.5 text-xs text-zinc-400 dark:text-zinc-500">
                      /10
                    </span>
                  </TableCell>

                  <TableCell>
                    {row.events.total > 0 ? (
                      <Badge
                        color={
                          row.events.passed === row.events.total ? "green" : "amber"
                        }
                      >
                        {row.events.passed}/{row.events.total}
                      </Badge>
                    ) : (
                      <span className="text-xs text-zinc-400 dark:text-zinc-500">
                        none asserted
                      </span>
                    )}
                  </TableCell>

                  <TableCell>
                    <Badge color={statusColor(row.evalRun?.status)}>
                      {row.evalRun?.status}
                    </Badge>
                   
                    {(row.evalRun?.status === "failed" ||
                      row.evalRun?.status === "stopped") &&
                      row.evalRun?.error && (
                        <p
                          className={`mt-1 max-w-xs text-xs ${
                            row.evalRun.status === "stopped"
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                          title={row.evalRun.error}
                        >
                          {row.evalRun.error}
                        </p>
                      )}
                  </TableCell>

                  <TableCell>
                   
                    {row.evalRun?.checkpointNumber ? (
                      <Button
                        plain
                        onClick={() => onViewCheckpoint(row)}
                        title="Prompt and assistant settings captured when this run executed"
                      >
                        View checkpoint
                      </Button>
                    ) : (
                      <span className="block px-2 py-1.5 text-sm text-zinc-400 dark:text-zinc-500">
                        {isSettling(row.evalRun?.status) ? "—" : "Not captured"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell>
                    <span className="tabular-nums text-sm text-zinc-600 dark:text-zinc-300">
                      {(row.evalRun?.messagesUsed ?? 0).toLocaleString()}
                    </span>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-x-1">
                    
                      <Button
                        color="zinc"
                        disabled={
                          row.evalRun?.status !== COMPLETED &&
                          row.evalRun?.status !== "stopped"
                        }
                        onClick={() => onViewRun(row.evalRun)}
                      >
                        Results
                      </Button>

                     
                      {!locked && !isSettling(row.evalRun?.status) && (
                        <Button
                          plain
                          title="Delete this run"
                          onClick={() => onDeleteRun(row.evalRun)}
                        >
                          <TrashIcon />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

      </div>
    </div>
  );
}
function RunCheckpoint(props) {
  const { evalRun, runNumber, workspaceId, onClose } = props;

  const {
    evalRun: detailedRun,
    isEvalRunLoading,
    fetchEvalRun,
  } = useEvalRun(workspaceId, evalRun?._id);

  useEffect(() => {
    if (workspaceId && evalRun?._id) {
      fetchEvalRun();
    }
  }, [workspaceId, evalRun?._id]);

  const snapshot = (detailedRun as any)?.settingsSnapshot;
  const config = (detailedRun as any)?.config ?? evalRun?.config ?? {};

  return (
    <Dialog className="" size="full" open={true} onClose={onClose}>
      <DialogTitle className="shrink-0 sm:*:w-auto *:w-auto p-4 border-none shadow-sm">
        Run #{runNumber} — checkpoint
      </DialogTitle>

      <div className="flex h-full min-h-0 flex-1 flex-col gap-y-8 overflow-auto p-4">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Captured {formatTime(evalRun?.createdAt)}. Both halves of the test are
          pinned here as they were when it ran — not as they look now.
        </p>

        {/* Two distinct groups: what the assistant was configured to do, and
            what the test graded it on. Without the grouping headings these read
            as one flat list and the last section looks like a duplicate. */}
        <Group
          title="Assistant settings at checkpoint"
          subtitle="The assistant configuration this run was pinned to"
        >
          {isEvalRunLoading ? (
            <p className="p-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
              Loading settings…
            </p>
          ) : !snapshot ? (
            <div className="rounded-lg border border-dashed border-zinc-300 p-6 text-center dark:border-zinc-600">
              <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                No checkpoint captured for this run
              </p>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                The checkpoint failed to save when this run started. The
                run itself was unaffected.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-y-6">
              <Section title="Prompt">
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-zinc-50 p-3 font-mono text-xs leading-relaxed text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200">
                  {snapshot?.modelSettings?.promptSettings?.rawPrompt ??
                    "No prompt recorded"}
                </pre>
              </Section>

              <Section title="Model settings">
                <FieldGrid
                  fields={[
                    [
                      "Response model",
                      getModelName(
                        snapshot?.modelSettings?.gptResponseModel ?? "—",
                      ),
                    ],
                    [
                      "Temperature",
                      String(snapshot?.modelSettings?.temperature ?? "—"),
                    ],
                    ["Timezone", snapshot?.timezoneSettings?.timezone ?? "—"],
                    [
                      "Transfer enabled",
                      snapshot?.transferSettings?.transferEnabled ? "Yes" : "No",
                    ],
                    [
                      "Office hours",
                      snapshot?.transferSettings?.officeHours ? "Yes" : "No",
                    ],
                    [
                      "Custom actions",
                      String((snapshot?.customActions?.actions ?? []).length),
                    ],
                  ]}
                />
              </Section>
            </div>
          )}
        </Group>

        <Group
          title="Test settings used for this run"
          subtitle="What the assistant was graded on"
        >
          <FieldGrid
            fields={[
              ["Mode", config?.simulationMode ?? "—"],
              ["Max turns", String(config?.maxTurns ?? "—")],
              ["Evaluator model", config?.evaluatorModel ?? "—"],
              ["Simulator model", config?.customerSimulatorModel ?? "—"],
              ["Simulated time", config?.simulatedTime || "real time"],
              ["End criteria", config?.endCriteria || "—"],
            ]}
          />

          <div className="mt-6">
            <Section title="Models tested">
              <div className="flex flex-wrap gap-2">
                {(config?.modelsToTest ?? []).map((model, idx) => (
                  <Badge key={idx}>{getModelName(model)}</Badge>
                ))}
              </div>
            </Section>
          </div>

          {/* Criteria and personas are not repeated here — the report already
              lists them against the scores they produced, which is where they're
              actually useful. */}
        </Group>
      </div>

      <DialogActions className="shrink-0 p-4">
        <Button plain onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function AutomatedTesting(props) {
  const {
    evalRuns,
    workspaceId,
    assistantId,
    locked,
    createEvalRun,
    runEvalTemplate,
    showPremiumOptions,
    enablePremiumOptions,
    showIndiaDeployedModels,
    newTestSignal,
    copyTestSignal,
    onViewChange,
    pagination,
    onPageChange,
  } = props;

  const [view, setView] = useState<string>(View.TEMPLATES);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [viewEvalRun, setViewEvalRun] = useState(undefined);
  const [checkpointRow, setCheckpointRow] = useState<any>(null);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isCopyOpen, setIsCopyOpen] = useState(false);

  const { templates: serverTemplates, fetchTemplates } = useEvalTemplates(
    workspaceId,
    assistantId,
  );

  useEffect(() => {
    if (workspaceId && assistantId) {
      fetchTemplates();
    }
  }, [workspaceId, assistantId, fetchTemplates, evalRuns]);

  const templates = useMemo(() => {
    const runsByTemplate = groupRunsByTemplate(evalRuns ?? []);

    return serverTemplates.map((template: any) => ({
      ...template,
      runs: runsByTemplate.get(String(template._id)) ?? [],
    }));
  }, [serverTemplates, evalRuns]);

  const activeTemplate = useMemo(
    () =>
      templates.find((template) => String(template._id) === activeTemplateId) ??
      null,
    [templates, activeTemplateId],
  );

  useEffect(() => {
    if (view !== View.TEMPLATES && !activeTemplate) {
      setView(View.TEMPLATES);
    }
  }, [view, activeTemplate]);

  // Opens a blank editor when the header's "New test" is pressed. Guarded on
  // > 0 so it doesn't fire on first mount.
  useEffect(() => {
    if (newTestSignal > 0) {
      setEditingTemplate(null);
      setIsEditorOpen(true);
    }
  }, [newTestSignal]);

  // Same for the header's "Copy from another assistant".
  useEffect(() => {
    if (copyTestSignal > 0) setIsCopyOpen(true);
  }, [copyTestSignal]);

  useEffect(() => {
    onViewChange?.(view);
  }, [view, onViewChange]);

  function handleOpenHistory(template) {
    setActiveTemplateId(String(template._id));
    setView(View.HISTORY);
  }


  function handleRunTemplate(template) {
    if (!template?._id) return;

    runEvalTemplate(template._id, {
      init: () => message.loading("Starting test run"),
      success: () => {
        message.destroy();
        message.success("Test started");
      },
      error: (e) => {
        message.destroy();
        showErrorToast(e, "Failed to start test run");
      },
    });
  }

  function handleEditTemplate(template) {
    setEditingTemplate(template ?? null);
    setIsEditorOpen(true);
  }

  function closeEditor() {
    setIsEditorOpen(false);
    setEditingTemplate(null);
  }

  async function handleSaveTemplate(formState, lifecycleCbs) {
    lifecycleCbs.init();

    try {
      const { name, ...config } = formState ?? {};
      const templateId = editingTemplate?._id;

      if (templateId) {
        await putUpdateEvalTemplate({
          params: { workspaceId, templateId },
          body: { name, config },
        });
      } else {
        await postCreateEvalTemplate({
          params: { workspaceId, assistantId },
          body: { name, config },
        });
      }

      await fetchTemplates();
      lifecycleCbs.success();
    } catch (e) {
      lifecycleCbs.error(e);
    }
  }

  
  async function handleSaveAndRun(formState, lifecycleCbs) {
    const templateId = editingTemplate?._id;

    if (!templateId) {
      // Brand new test: one call creates the template and starts the run.
      return createEvalRun(formState, lifecycleCbs);
    }

    lifecycleCbs.init();

    try {
      const { name, ...config } = formState ?? {};

      await putUpdateEvalTemplate({
        params: { workspaceId, templateId },
        body: { name, config },
      });

      await fetchTemplates();

      await runEvalTemplate(templateId, {
        init: () => {},
        success: () => lifecycleCbs.success(),
        error: (e) => lifecycleCbs.error(e),
      });
    } catch (e) {
      lifecycleCbs.error(e);
    }
  }

  /**
   * Deactivate or reactivate a test.
   *
   * Nothing is destroyed: a deactivated test keeps its runs, scores and the
   * messages it was billed for, and can be switched back on. That's why this
   * needs no confirmation — unlike the delete it replaced, it's reversible.
   */
  async function handleSetTemplateActive(template, isActive, lifecycleCbs) {
    if (!template?._id) return;

    lifecycleCbs?.init?.();

    try {
      await patchEvalTemplateActive({
        params: { workspaceId, templateId: template._id },
        body: { isActive },
      });

      if (!isActive) {
        setView(View.TEMPLATES);
        setActiveTemplateId(null);
      }

      await fetchTemplates();
      lifecycleCbs?.success?.();
    } catch (e) {
      lifecycleCbs?.error?.(e);
    }
  }

  /** Delete one run. Its transcript and score go; the test itself stays. */
  async function handleDeleteRun(evalRun, lifecycleCbs) {
    if (!evalRun?._id) return;

    lifecycleCbs?.init?.();

    try {
      await deleteEvalRun({
        params: { workspaceId, evalRunId: evalRun._id },
      });

      await Promise.all([onPageChange?.(pagination?.page ?? 1), fetchTemplates()]);

      lifecycleCbs?.success?.();
    } catch (e) {
      lifecycleCbs?.error?.(e);
    }
  }

  /**
   * Copy a test from another assistant onto this one.
   *
   * No name is sent: the backend names the copy after the assistant it came
   * from, and renaming is one click away under Edit settings. The route still
   * accepts an optional `name` for API-key callers.
   */
  async function handleCopyTemplate(sourceTemplateId, lifecycleCbs) {
    if (!sourceTemplateId) return;

    lifecycleCbs?.init?.();

    try {
      await postCopyEvalTemplate({
        params: { workspaceId, assistantId },
        body: { sourceTemplateId },
      });

      await fetchTemplates();
      lifecycleCbs?.success?.();
    } catch (e) {
      lifecycleCbs?.error?.(e);
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-y-4 overflow-auto py-2">
      {view === View.TEMPLATES && (
        <TemplatesTable
          locked={locked}
          templates={templates}
          onOpenHistory={handleOpenHistory}
          onRunTemplate={handleRunTemplate}
          onEditTemplate={handleEditTemplate}
          onSetTemplateActive={handleSetTemplateActive}
        />
      )}

      {view === View.HISTORY && activeTemplate && (
        <RunHistory
          locked={locked}
          template={activeTemplate}
          onBack={() => setView(View.TEMPLATES)}
          onViewRun={setViewEvalRun}
          onViewCheckpoint={setCheckpointRow}
          onRunTemplate={handleRunTemplate}
          onEditTemplate={handleEditTemplate}
          onDeleteRun={handleDeleteRun}
        />
      )}

      {viewEvalRun && (
        <EvalRunDetail
          workspaceId={workspaceId}
          evalRun={viewEvalRun}
          onClose={() => setViewEvalRun(undefined)}
        />
      )}

      {checkpointRow && (
        <RunCheckpoint
          evalRun={checkpointRow.evalRun}
          runNumber={checkpointRow.runNumber}
          workspaceId={workspaceId}
          onClose={() => setCheckpointRow(null)}
        />
      )}

      {isEditorOpen && (
        <EvalRunCreateDialog
          title={editingTemplate ? "Edit test" : "New test"}
          evalRunConfig={
            editingTemplate
              ? { ...editingTemplate.config, name: editingTemplate.name }
              : undefined
          }
          showPremiumOptions={showPremiumOptions}
          enablePremiumOptions={enablePremiumOptions}
          showIndiaDeployedModels={showIndiaDeployedModels}
          onCreateEvalRun={handleSaveAndRun}
          onSaveTemplate={handleSaveTemplate}
          onClose={closeEditor}
        />
      )}

      {isCopyOpen && (
        <EvalTemplateCopy
          workspaceId={workspaceId}
          assistantId={assistantId}
          onConfirmCopy={handleCopyTemplate}
          onClose={() => setIsCopyOpen(false)}
        />
      )}
    </div>
  );
}
